const KeenSlider = window.KeenSlider

const { setInitialised, useA11y, useArrows, useStrictSlideVisibilityA11y } =
  window.BAO.Carousel.plugins
const { setPreInitSlideA11yState } = window.BAO.Carousel

class CtaCarousel extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    this.carousel = this.setupCarousel()
  }

  setupCarousel () {
    setPreInitSlideA11yState(this.els.slides.element, this.els.slide.selector)

    const carousel = new KeenSlider(
      this.els.slides.element,
      {
        selector: this.els.slide.selector,
        defaultAnimation: {
          duration: 1500,
        },
        loop: false,
        mode: 'snap',
        slides: {
          perView: this.dataset.slides - 2.8,
          spacing: 10,
        },
        breakpoints: {
          '(min-width: 768px)': {
            slides: {
              perView: this.dataset.slides - 2,
              spacing: 20,
            },
          },
          '(min-width: 1201px)': {
            slides: {
              perView: this.dataset.slides,
              spacing: 20,
            },
          },
        },
      },
      [
        setInitialised(),
        useA11y({
          includeTabindex: false,
        }),
        useStrictSlideVisibilityA11y(0.95),
        useArrows({
          next: this.els?.next?.element,
          previous: this.els?.previous?.element,
        }),
      ],
    )

    this.setupArrowFocusManagement(carousel)

    return carousel
  }

  setupArrowFocusManagement (carousel) {
    let pendingFocusMove = false
    let lastFocusedSlideIndex = null
    let pendingTargetSlideIndex = null
    let pendingTabFocusTarget = null
    let pendingTabIntent = false
    let isKeyboardArrowActivation = false
    const focusableSelector =
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'

    const resolveTargetFocusable = () => {
      if (!Number.isInteger(pendingTargetSlideIndex)) {
        pendingTabFocusTarget = null
        return
      }

      const targetSlide = carousel.slides[pendingTargetSlideIndex]
      const firstFocusable = targetSlide?.querySelector(focusableSelector)

      pendingTabFocusTarget = firstFocusable || null

      if (pendingTabIntent && pendingTabFocusTarget) {
        pendingTabFocusTarget.focus({ preventScroll: true })
        pendingTabFocusTarget = null
        pendingTabIntent = false
      }
    }

    const onCarouselFocusIn = (event) => {
      const focusedSlide = event.target.closest(this.els.slide.selector)

      if (!focusedSlide) {
        return
      }

      lastFocusedSlideIndex = carousel.slides.indexOf(focusedSlide)
    }

    const resolvePendingTarget = () => {
      if (!pendingFocusMove) {
        return
      }

      pendingFocusMove = false
      resolveTargetFocusable()
    }

    carousel.on('animationEnded', resolvePendingTarget)

    this.els.slides.element.addEventListener('focusin', onCarouselFocusIn)

    const onArrowActivate = (direction, event) => {
      const wasKeyboardActivation =
        isKeyboardArrowActivation || event?.detail === 0

      isKeyboardArrowActivation = false

      if (!wasKeyboardActivation) {
        pendingFocusMove = false
        pendingTargetSlideIndex = null
        pendingTabFocusTarget = null
        pendingTabIntent = false
        return
      }

      const fallbackIndex = Number.isInteger(carousel.track?.details?.rel)
        ? carousel.track.details.rel
        : 0
      const baseSlideIndex = Number.isInteger(lastFocusedSlideIndex)
        ? lastFocusedSlideIndex
        : fallbackIndex
      const maxSlideIndex = carousel.slides.length - 1

      pendingTargetSlideIndex =
        direction === 'previous'
          ? Math.max(baseSlideIndex - 1, 0)
          : Math.min(baseSlideIndex + 1, maxSlideIndex)

      pendingFocusMove = true
    }

    const onArrowKeydown = (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        isKeyboardArrowActivation = true
        return
      }

      if (event.key !== 'Tab' || event.shiftKey) {
        return
      }

      if (!pendingTabFocusTarget && !pendingFocusMove) {
        return
      }

      event.preventDefault()

      if (pendingTabFocusTarget) {
        pendingTabFocusTarget.focus({ preventScroll: true })
        pendingTabFocusTarget = null
        pendingTabIntent = false
        return
      }

      pendingTabIntent = true
    }

    this.els?.next?.element?.addEventListener(
      'click',
      (event) => {
        onArrowActivate('next', event)
      },
      true,
    )
    this.els?.previous?.element?.addEventListener(
      'click',
      (event) => {
        onArrowActivate('previous', event)
      },
      true,
    )

    this.els?.next?.element?.addEventListener('keydown', onArrowKeydown)
    this.els?.previous?.element?.addEventListener('keydown', onArrowKeydown)
  }
}

if (!customElements.get('cta-carousel')) {
  customElements.define('cta-carousel', CtaCarousel)
}
